import * as THREE from 'three';
import { bronze, canDraw } from './hud-materials.js';
import { boltGeometry, iron } from './hud-parts.js';
import { plaqueGeometry } from './hud-chart.js';
import { unpickable } from './radial-menu.js';

/** The plaque's height, in CSS pixels at size 1. */
const HEIGHT = 34;
const CANVAS = { width: 1024, height: 64 };

/**
 * A status line on a bronze plaque, the letters cut into it: the combat status and Paused at the top of the screen. It grows to
 * its text, drops in from above when it appears and lifts away when its text goes. Units are CSS pixels; the HUD places it.
 */
export class HudBanner extends THREE.Group {
  private metal = Object.assign(bronze(73, 0x5a452c), { flatShading: true });
  private hardware = iron();
  private plate = new THREE.Mesh(plaqueGeometry(100, HEIGHT), this.metal);
  private canvas = canDraw() ? Object.assign(document.createElement('canvas'), CANVAS) : null;
  private texture = this.canvas ? new THREE.CanvasTexture(this.canvas) : null;
  private label = unpickable(new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: this.texture, transparent: true, depthWrite: false })));
  private bolt = boltGeometry(3);
  private bolts: THREE.Mesh[];
  private text = '';
  private shown = 0;

  /** `ink` colours the letters; `title` cuts them in the game's title face rather than its text face. */
  constructor(private ink: string, private title = false) {
    super();
    if (this.texture) this.texture.colorSpace = THREE.SRGBColorSpace; else this.label.visible = false;
    this.label.position.z = 7;
    this.bolts = [-1, 1].map(() => new THREE.Mesh(this.bolt, this.hardware));
    for (const bolt of this.bolts) bolt.position.z = 6;
    this.add(this.plate, this.label, ...this.bolts);
    this.visible = false;
  }

  /** Shows a line (null lifts the plaque away). */
  setText(text: string | null) {
    if (text === null) { this.wanted = false; return; }
    this.wanted = true;
    if (text === this.text) return;
    this.text = text;
    const canvas = this.canvas; if (!canvas || !this.texture) return;
    const ctx = canvas.getContext('2d')!, size = this.title ? 50 : 46;
    ctx.font = this.title ? `400 ${size}px 'Metal Mania'` : `600 ${size}px Dosis`;
    const width = Math.min(canvas.width - 20, ctx.measureText(this.title ? spaced(text) : text).width + 8);
    ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const line = this.title ? spaced(text) : text;
    // Cut into the bronze: a dark lower edge under the letters.
    ctx.fillStyle = '#120c06'; ctx.fillText(line, canvas.width / 2, canvas.height / 2 + 2, canvas.width - 20);
    ctx.fillStyle = this.ink; ctx.fillText(line, canvas.width / 2, canvas.height / 2, canvas.width - 20);
    this.texture.needsUpdate = true;
    // The plaque fits the text: the label shows the whole canvas at the text's scale, the plate stretches to the words.
    const scale = HEIGHT * .92 / canvas.height, span = width * scale + HEIGHT * 1.8;
    this.label.scale.set(canvas.width * scale, canvas.height * scale, 1);
    this.plate.scale.x = span / 100;
    this.bolts[0].position.x = -span / 2 + HEIGHT * .55; this.bolts[1].position.x = span / 2 - HEIGHT * .55;
  }
  private wanted = false;

  update(dt: number) {
    this.shown += ((this.wanted ? 1 : 0) - this.shown) * Math.min(1, dt * 10);
    this.visible = this.shown > .01;
    // It drops a little into place, as if hung on a chain.
    this.position.y += (1 - this.shown) * 16;
    this.scale.multiplyScalar(.85 + this.shown * .15);
  }

  dispose() {
    this.plate.geometry.dispose(); this.label.geometry.dispose(); (this.label.material as THREE.Material).dispose(); this.texture?.dispose();
    this.metal.dispose(); this.hardware.dispose(); this.bolt.dispose();
  }
}

/** Letters spaced out for an engraved title. */
const spaced = (text: string) => text.split('').join(' ');
